import type {
  SmartRetryError,
  SmartRestartControllerConfig,
  SmartRestartStep,
} from './types'

import {
  MANUAL_RETRY_COOLDOWN_MS,
  MAX_RETRIES_DRM,
  MAX_RETRIES_NON_DRM,
  NO_RETRY_ERROR_CODES,
  RECOVERABLE_ERROR_CODES,
  RETRY_DELAY_DRM_MS,
  RETRY_DELAY_NON_DRM_MS,
} from './constants'

/**
 * Platform-agnostic Smart-Restart recovery state machine.
 *
 * Implements the CR-404 retry lifecycle:
 *
 * PLAYING ──- (recoverable error) ──► RETRYING -── (success) ──► PLAYING
 *                                                  └── (exhausted) -─► ERROR
 *
 * The retry budget is scoped to the player *session*, not to a single content.
 * It is consumed across content switches (switching to another failing channel
 * does not grant a fresh budget) and is only replenished on successful playback
 * or a full session reset (player closed / reopened).
 *
 * Usage:
 *   const ctrl = new SmartRestartController({ isDrm, onRetry, onStepChange })
 *   ctrl.handleError(error)             // on every player error
 *   ctrl.handleManualRetryStart()       // when the user presses the Retry button (ERROR step)
 *   ctrl.handlePlaybackRestored()       // on successful playback start
 *   ctrl.handleContentSwitch()          // when user changes content mid-session
 *   ctrl.reset()                        // when player is fully closed / reopened
 *   ctrl.dispose()                      // cleanup — call in effect cleanup / unmount
 */
export class SmartRestartController {
  private _step: SmartRestartStep = 'PLAYING'

  /** True while a manual retry (triggered by the Retry button) is in-flight. */
  private _isManualRetry = false

  /** Automatic retries consumed in the current recovery session. */
  private _retryCount = 0

  /**
   * True once the session hit its automatic retry limit. Stays set across
   * content switches so that switching to a higher-budget stream (e.g. DRM→
   * non-DRM) does not grant a new automatic retry cycle (CR-404 recovery
   * session). Cleared only on successful playback or full reset.
   */
  private _limitReached = false

  /** Pending retry timer handle for the active session. */
  private _timerId: ReturnType<typeof setTimeout> | null = null

  /** Timestamp until which the next attempt is blocked; null when unblocked. */
  private _attemptAllowedAfter: number | null = null

  private _isDrm: boolean

  /**
   * Whether the current DRM stream's key systems are supported by the platform.
   * When the stream is DRM-protected but unsupported (no matching key system in
   * the url-response `drm.la`), Smart-Restart offers neither automatic retries
   * nor the Retry button (CR-404 AC-6 / step 10). Irrelevant for non-DRM.
   */
  private _isDrmSupported: boolean

  private readonly _onRetry: () => void
  private readonly _onStepChange: (step: SmartRestartStep) => void
  private readonly _onRecoveryAbort?: () => void

  /**
   * Creates a controller bound to platform callbacks.
   * Retry budget depends on isDrm (CR-404 FR-1).
   */
  constructor({
    isDrm,
    isDrmSupported = true,
    onRetry,
    onStepChange,
    onRecoveryAbort,
  }: SmartRestartControllerConfig) {
    this._isDrm = isDrm
    this._isDrmSupported = isDrmSupported
    this._onRetry = onRetry
    this._onStepChange = onStepChange
    this._onRecoveryAbort = onRecoveryAbort
  }

  /**
   * Current Smart-Restart lifecycle step exposed to the UI adapter.
   * One of PLAYING, RETRYING, or ERROR.
   */
  get step(): SmartRestartStep {
    return this._step
  }

  /**
   * Automatic retry attempts already consumed in the current session.
   * Resets when playback is restored or the session is reset.
   */
  get retryCount(): number {
    return this._retryCount
  }

  /**
   * Maximum automatic retries before surfacing ERROR to the user.
   * DRM streams use MAX_RETRIES_DRM; non-DRM use MAX_RETRIES_NON_DRM.
   */
  get maxRetries(): number {
    return this._isDrm ? MAX_RETRIES_DRM : MAX_RETRIES_NON_DRM
  }

  /**
   * Milliseconds until the next retry is allowed; null when none is pending.
   * Used by the manual Retry button countdown (CR-404 FR-4).
   */
  get remainingRetryDelayMs(): number | null {
    if (this._attemptAllowedAfter == null) {
      return null
    }

    return Math.max(0, this._attemptAllowedAfter - Date.now())
  }

  /**
   * Syncs the DRM flag (and platform support) when the stream's DRM status is
   * resolved from the url response. `isSupported` reflects whether any of the
   * stream's key systems are playable on this platform (CR-404 AC-6).
   * The updated values are picked up on the next error.
   */
  handleDrm(isDrm: boolean, isSupported = true): void {
    this._isDrm = isDrm
    this._isDrmSupported = isSupported
  }

  /**
   * True when the current stream is DRM-protected but none of its key systems
   * are supported by the platform. Such a stream can never recover, so it skips
   * automatic retries and the Retry button (CR-404 AC-6 / step 10).
   */
  private isUnsupportedDrm(): boolean {
    return this._isDrm && !this._isDrmSupported
  }

  /**
   * Returns true when the error code is eligible for automatic recovery.
   * Non-recoverable codes (DRM, geo, live expiry) are rejected (CR-404 FR-1).
   */
  isRecoverableError(error: SmartRetryError | null): boolean {
    return error !== null && RECOVERABLE_ERROR_CODES.has(error.errorCode)
  }

  /**
   * Adapter entry point for every player/API playback error.
   * Drives PLAYING → RETRYING → ERROR per CR-404 steps 1, 9, and 10.
   */
  handleError(error: SmartRetryError): void {
    if (this.isUnsupportedDrm()) {
      this.handleUnsupportedDrmError()

      return
    }

    if (!this.isRecoverableError(error)) {
      this.handleNonRecoverableError(error)

      return
    }

    if (error.isNative) {
      this.handleNativeModeError()

      return
    }

    if (this._step === 'PLAYING') {
      this.handleErrorWhilePlaying()

      return
    }

    if (this._step === 'RETRYING') {
      this.handleErrorWhileRetrying()
    }
  }

  /**
   * Unsupported-DRM error (CR-404 AC-6 / step 10): the platform cannot decrypt
   * this stream, so there are no automatic retries and no Retry button. The
   * player surfaces its own error overlay; Smart-Restart stays in PLAYING and
   * never emits the ERROR step that would show the button.
   */
  private handleUnsupportedDrmError(): void {
    this.cancelPendingRetry()
    this._isManualRetry = false

    if (this._step !== 'PLAYING') {
      this.toStep('PLAYING')
    }
  }

  /**
   * Native playback mode (CR-404 AC-9 / step 1.2): the browser plays the media
   * directly (no MSE/VHS pipeline), so the app cannot reliably drive automatic
   * retries. Transition straight to ERROR — the error screen and Retry button
   * are shown so the user can still trigger a manual recovery. A failed manual
   * retry still applies the flat 30s cooldown (FR-4).
   */
  private handleNativeModeError(): void {
    this.cancelPendingRetry()

    if (this._isManualRetry) {
      this._isManualRetry = false
      this.applyManualRetryCooldown()
    }

    this.toStep('ERROR')
  }

  /**
   * Non-recoverable error: abort mid-retry and surface immediately.
   * FP-4176 ER no-retry codes (DR001/SE404/…): keep PLAYING so the player
   * error overlay shows without the Smart-Restart Retry button.
   */
  private handleNonRecoverableError(error: SmartRetryError): void {
    if (NO_RETRY_ERROR_CODES.has(error.errorCode)) {
      const wasRecovering = this._step !== 'PLAYING'

      this.clearTimer()
      this._isManualRetry = false

      if (wasRecovering) {
        this.toStep('PLAYING')
        this._onRecoveryAbort?.()
      }

      return
    }

    if (this._step === 'RETRYING') {
      this.clearTimer()
      this.toStep('ERROR')
    }
  }

  /** Recoverable error received while in PLAYING step (CR-404 steps 1 and 9). */
  private handleErrorWhilePlaying(): void {
    if (this.isBudgetExhausted()) {
      /*
       * Session already exhausted its retry budget (e.g. on the previous
       * content). Go straight to ERROR so the Retry button shows.
       */
      this._limitReached = true
      this.toStep('ERROR')

      return
    }

    this.toStep('RETRYING')
    this.scheduleRetry()
  }

  /** Recoverable error received during a retry attempt (CR-404 step 10). */
  private handleErrorWhileRetrying(): void {
    if (this.isBudgetExhausted()) {
      this._limitReached = true

      if (this._isManualRetry) {
        // Manual retry failed — apply flat 30s cooldown (CR-404 AC-4 / step 13).
        this._isManualRetry = false
        this.applyManualRetryCooldown()
      }

      this.toStep('ERROR')

      return
    }

    /*
     * Failed attempt but budget remains.
     * Schedule next retry with fixed delay.
     */
    this.scheduleRetry()
  }

  /**
   * True when the session can no longer auto-retry: either the limit was already
   * reached earlier in the session, or the current attempt count hit the budget.
   * The sticky flag matters when the stream's max changes mid-session (DRM↔non-DRM).
   */
  private isBudgetExhausted(): boolean {
    return this._limitReached || this._retryCount >= this.maxRetries
  }

  /**
   * Manual Retry button (CR-404 step 11): ERROR → RETRYING.
   * Adapter refetches after this; handlePlaybackRestored() clears the counter.
   */
  handleManualRetryStart(): void {
    if (this._step !== 'ERROR') {
      return
    }

    this._isManualRetry = true
    this.toStep('RETRYING')
  }

  /**
   * Called when playback resumes after any successful start.
   * CR-404 step 8/12: clears the retry budget and returns to PLAYING so the
   * next failure starts a fresh recovery session. Returns true when a recovery
   * was actually in progress (so the UI can emit SmartRestartSuccess).
   */
  handlePlaybackRestored(): boolean {
    const wasRecovering = this._step !== 'PLAYING' || this._retryCount > 0

    this._isManualRetry = false
    this._retryCount = 0
    this._limitReached = false
    this.clearTimer()

    if (this._step !== 'PLAYING') {
      this.toStep('PLAYING')
    }

    return wasRecovering
  }

  /**
   * Called when the user switches content mid-session.
   * Cancels the previous content's pending retry and resets the step so the new
   * content re-enters the state machine, but preserves the session retry budget
   * AND any active manual-retry cooldown (CR-404 AC-3 / recovery session):
   * switching to another failing content does not grant fresh retries and keeps
   * the running Retry-button countdown.
   */
  handleContentSwitch(): void {
    this.cancelPendingRetry()
    this._isManualRetry = false

    if (this._step !== 'PLAYING') {
      this.toStep('PLAYING')
    }
  }

  /**
   * Full session reset when the player closes and reopens.
   * Clears the timer, counter, and returns to PLAYING.
   */
  reset(): void {
    this.clearTimer()
    this._retryCount = 0
    this._limitReached = false
    this._isManualRetry = false
    this._isDrmSupported = true
    this.toStep('PLAYING')
  }

  /**
   * Releases timer resources when the controller instance is discarded.
   * Call from effect cleanup or component unmount.
   */
  dispose(): void {
    this.clearTimer()
  }

  /*
   * ---------------------------------------------------------------------------
   * Private methods
   * ---------------------------------------------------------------------------
   */

  /**
   * Fixed retry delay for the current stream type.
   */
  private retryDelayMs(): number {
    return this._isDrm ? RETRY_DELAY_DRM_MS : RETRY_DELAY_NON_DRM_MS
  }

  /**
   * Applies a fixed 30-second cooldown after a failed manual retry.
   * CR-404 AC-4 / step 13 — blocks Retry until cooldown expires.
   */
  private applyManualRetryCooldown(): void {
    this._attemptAllowedAfter = Date.now() + MANUAL_RETRY_COOLDOWN_MS
  }

  /**
   * Schedules the retry timer. invokes onRetry when the delay elapses.
   */
  private scheduleRetry(): void {
    const nextCount = this._retryCount + 1
    const isFirstAttempt = nextCount === 1
    const delayMs = isFirstAttempt ? 0 : this.retryDelayMs()

    this.clearTimer()
    this._retryCount = nextCount
    this._attemptAllowedAfter = isFirstAttempt ? null : Date.now() + delayMs
    this._timerId = setTimeout(() => this.executeRetry(), delayMs)
  }

  /**
   * Fires when a scheduled retry timer elapses.
   * Clears the timer handle then invokes the platform onRetry callback.
   */
  private executeRetry(): void {
    this._timerId = null
    this._attemptAllowedAfter = null
    this._onRetry()
  }

  /**
   * Updates internal step and notifies the adapter via onStepChange.
   * All lifecycle transitions must go through this helper.
   */
  private toStep(next: SmartRestartStep): void {
    this._step = next
    this._onStepChange(next)
  }

  /**
   * Cancels the pending retry timeout without touching the attempt gate.
   * Used on content switch to keep an active manual-retry cooldown running.
   */
  private cancelPendingRetry(): void {
    if (this._timerId != null) {
      clearTimeout(this._timerId)
      this._timerId = null
    }
  }

  /**
   * Cancels the pending retry timer and clears the attempt gate.
   */
  private clearTimer(): void {
    this.cancelPendingRetry()

    this._attemptAllowedAfter = null
  }
}
